import React, { useMemo, useState, useEffect, useRef } from "react";
import cityTimezones from "city-timezones";
import { MapPin, X } from "lucide-react";

/**
 * CitySelector
 * ────────────
 * Searchable city → IANA timezone picker. Backed by `city-timezones` (offline
 * dataset ~148k cities) so it works without any API key or network.
 *
 * Props:
 *   value    — { city, state, country, timezone } | null
 *   onChange — called with the same shape (or null when cleared)
 *   testid   — data-testid prefix for the input
 *   error    — optional inline error string
 *
 * The consumer decides when to persist. Users cannot type free text — they
 * must select from the dropdown, guaranteeing every stored value has a valid
 * IANA timezone. There is NO silent UTC fallback: if the location dataset does
 * not resolve a timezone for a match, that suggestion is filtered out.
 *
 * Displayed suggestion format: "City, State/Region, Country" (with disambiguation
 * to handle duplicate city names).
 */
export default function CitySelector({ value, onChange, testid = "city-input", error }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef(null);

  const displayValue = value
    ? `${value.city}${value.state ? ", " + value.state : ""}${value.country ? ", " + value.country : ""}`
    : "";

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = (query || "").trim();
    if (q.length < 2) return [];
    // The library performs case-insensitive prefix search on city name.
    const raw = cityTimezones.lookupViaCity(q) || [];
    return raw
      .filter((r) => !!r.timezone) // No silent UTC fallback — reject entries without an IANA zone
      .slice(0, 20)
      .map((r) => ({
        city: r.city,
        state: r.province || r.state_ansi || "",
        country: r.country,
        timezone: r.timezone,
        // Compact disambiguated label
        label: `${r.city}${r.province ? ", " + r.province : ""}${r.country ? ", " + r.country : ""}`,
      }));
  }, [query]);

  const select = (s) => {
    onChange({ city: s.city, state: s.state, country: s.country, timezone: s.timezone });
    setQuery("");
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery("");
    setOpen(true);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter")   { e.preventDefault(); select(suggestions[highlight]); }
    else if (e.key === "Escape")  { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      {value ? (
        <div
          className="flex items-center justify-between gap-2 rounded-xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]/50 px-3 py-2.5"
          data-testid={`${testid}-selected`}
        >
          <div className="flex items-start gap-2 min-w-0">
            <MapPin size={16} className="mt-0.5 text-[var(--panini-red)] flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-bold truncate">{displayValue}</div>
              <div className="text-[11px] text-[var(--smoke)] font-mono">Time zone: {value.timezone}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-[var(--smoke)] hover:text-[var(--ink)]"
            data-testid={`${testid}-clear`}
            aria-label="Change city"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKey}
              placeholder="Start typing a city — e.g. Miami"
              className={`w-full rounded-xl border-2 pl-9 pr-3 py-2.5 text-sm ${
                error ? "border-[var(--panini-red)]" : "border-[var(--ink)]"
              }`}
              autoComplete="off"
              data-testid={testid}
            />
          </div>
          {open && suggestions.length > 0 && (
            <ul
              className="absolute left-0 right-0 mt-1 z-20 max-h-64 overflow-auto rounded-xl border-2 border-[var(--ink)] bg-white shadow-lg"
              data-testid={`${testid}-suggestions`}
            >
              {suggestions.map((s, i) => (
                <li
                  key={`${s.city}-${s.state}-${s.country}-${i}`}
                  onMouseDown={(e) => { e.preventDefault(); select(s); }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`px-3 py-2 text-sm cursor-pointer ${
                    i === highlight ? "bg-[var(--panini-yellow-soft)]" : "hover:bg-[var(--panini-yellow-soft)]"
                  }`}
                  data-testid={`${testid}-suggestion-${i}`}
                >
                  <div className="font-bold">{s.label}</div>
                  <div className="text-[10px] text-[var(--smoke)] font-mono">{s.timezone}</div>
                </li>
              ))}
            </ul>
          )}
          {open && query.length >= 2 && suggestions.length === 0 && (
            <div className="absolute left-0 right-0 mt-1 z-20 rounded-xl border-2 border-[var(--panini-red)] bg-white p-3 text-xs text-[var(--panini-red)]">
              No matching cities. Try a nearby major city (we can&apos;t determine your time zone without a valid location).
            </div>
          )}
        </>
      )}
      {error && !value && (
        <div className="mt-1 text-xs text-[var(--panini-red)] font-bold">{error}</div>
      )}
    </div>
  );
}
